/**
 * S-18 Roulette — "the pit" (V6, SM-8) — v3 "AFTER-DARK ARCADE".
 *
 * The game layer at full volume: CRT marquee, neon wheel matchmaking, an
 * announcer sting when a stranger lands, arcade verdict controls. Safety
 * surfaces stay dead-serious and always visible (copy-tone §1.4): Report /
 * Block / Leave keep their calm styling in-session and on the end card;
 * recourse never requires the partner's profile.
 *
 * Gate screen (rules card + evidence-buffer consent line + verified-only gate
 * + recent-partners recourse list) → queue (rotating searching lines, cancel,
 * 5-min timeout) → live session (text always; optional relay-only AV when the
 * server flag allows; Skip / Vibe Check / Report / Block / Leave always
 * visible) → REQUEUE_GATE end card (report/block one tap away, skip-velocity
 * 30 s countdown when tripped).
 *
 * Presentation + motion only: WS command plane, AV bring-up and the status
 * recovery plane are byte-identical to v2. Every push is a re-render hint;
 * GET /roulette/status is the recovery plane.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { copy, formatCopy, resolveCopy } from "@humble/shared";
import { AnimatePresence, motion } from "framer-motion";
import { announce, Badge, Button, Card, springs, VerifiedBadge, toast } from "../../design/index.js";
import { isApiError } from "../../lib/api.js";
import { ws, wsCommand, WsCommandError } from "../../lib/ws.js";
import { BlockConfirmSheet, ReportSheet } from "../safety/report-sheet.js";
import { useFeatureToggles } from "../settings/use-feature-toggles.js";
import { fetchRouletteIce, handleRemoteIce, handleRemoteSdp, startRouletteAv, type RouletteAvSession } from "./av.js";
import { rouletteApi } from "./raw-api.js";
import "./roulette.css";

// ---------------------------------------------------------------------------
// wire types (mirror sm8_roulette statusView / §4.6 payloads)
// ---------------------------------------------------------------------------
interface RecentPartner {
  session_id: string;
  display_name: string;
  ended_at: string;
}
interface Status {
  eligible: boolean;
  reason: string | null;
  verified: boolean;
  av_enabled: boolean;
  state: "idle" | "queued" | "in_session";
  gate_until: string | null;
  restricted_until: string | null;
  session: {
    session_id: string;
    state: string;
    mode: string;
    partner: { display_name: string; age: number | null; verified: boolean };
    vibe_sent: boolean;
  } | null;
  recent_partners: RecentPartner[];
}
interface ChatLine {
  key: string;
  from: "me" | "partner";
  body: string;
  folded?: boolean;
  revealed?: boolean;
}

type Phase = "loading" | "gate" | "queued" | "session" | "ended";

interface EndCardState {
  session_id: string;
  partner_display_name: string;
  reason_class: string;
}

function useCountdownSeconds(until: string | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!until) {
      setLeft(0);
      return;
    }
    const compute = () => Math.max(0, Math.ceil((Date.parse(until) - Date.now()) / 1000));
    setLeft(compute());
    const t = setInterval(() => setLeft(compute()), 500);
    return () => clearInterval(t);
  }, [until]);
  return left;
}

/** The neon matchmaking wheel — pure cosmetics behind the queue copy. */
function PitWheel() {
  return (
    <div className="roulette__spinner" aria-hidden="true">
      <span className="roulette__spinner-ring" />
      <span className="roulette__spinner-ring roulette__spinner-ring--counter" />
      <span className="roulette__spinner-core" />
    </div>
  );
}

export function RouletteScreen() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("loading");
  const [status, setStatus] = useState<Status | null>(null);
  const [session, setSession] = useState<Status["session"] | null>(null);
  const [initiator, setInitiator] = useState(false);
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState("");
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [vibeSent, setVibeSent] = useState(false);
  const [endCard, setEndCard] = useState<EndCardState | null>(null);
  const [gateUntil, setGateUntil] = useState<string | null>(null);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [searchLine, setSearchLine] = useState(0);
  const [camOn, setCamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [reportFor, setReportFor] = useState<{ sessionId: string; name: string } | null>(null);
  const [blockFor, setBlockFor] = useState<{ sessionId: string; name: string } | null>(null);
  const [pendingAnyway, setPendingAnyway] = useState<string | null>(null);
  // S-27 game toggles: pit OFF ⇒ the entry affordance is replaced by a
  // settings pointer (server refuses regardless — ROULETTE_DISABLED).
  const rouletteEnabled = useFeatureToggles().roulette_enabled;

  const gateSeconds = useCountdownSeconds(gateUntil);
  const logRef = useRef<HTMLDivElement>(null);
  const avRef = useRef<RouletteAvSession | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<string | null>(null);
  sessionRef.current = session?.session_id ?? null;

  const stopAv = useCallback(() => {
    avRef.current?.close();
    avRef.current = null;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await rouletteApi<Status>("GET", "/roulette/status");
      setStatus(s);
      setGateUntil(s.gate_until);
      if (s.state === "in_session" && s.session) {
        setSession(s.session);
        setVibeSent(s.session.vibe_sent);
        setPhase("session");
      } else if (s.state === "queued") {
        setPhase("queued");
      } else {
        setPhase((p) => (p === "ended" ? p : "gate"));
      }
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
      setPhase("gate");
    }
  }, []);

  // ── realtime wiring ────────────────────────────────────────────────────────
  useEffect(() => {
    ws.ensureConnected();
    void refresh();

    const offs = [
      ws.on("roulette.queued", () => {
        setQueueEmpty(false);
        setPhase("queued");
      }),
      ws.on("roulette.paired", (p) => {
        setLines([]);
        setPartnerTyping(false);
        setVibeSent(false);
        setEndCard(null);
        setInitiator(Boolean((p as { initiator?: boolean }).initiator));
        setSession({
          session_id: p.session_id,
          state: "connecting",
          mode: p.mode,
          partner: p.partner as Status["session"] extends infer S
            ? S extends { partner: infer P }
              ? P
              : never
            : never,
          vibe_sent: false,
        });
        setPhase("session");
        // Announcer sting — cosmetic, respects volume + reduced-motion (S-22).
        announce({
          sting: resolveCopy("roulette.paired_sting") ?? "STRANGER ACQUIRED",
          motif: "accent",
          durationMs: 2200,
        });
      }),
      ws.on("roulette.live", (p) => {
        setSession((s) => (s && s.session_id === p.session_id ? { ...s, state: "live" } : s));
      }),
      ws.on("roulette.msg", (p) => {
        if (p.session_id !== sessionRef.current) return;
        setPartnerTyping(false);
        setLines((ls) => [
          ...ls,
          { key: crypto.randomUUID(), from: "partner", body: p.body, folded: p.folded },
        ]);
      }),
      ws.on("roulette.typing", (p) => {
        if (p.session_id !== sessionRef.current) return;
        setPartnerTyping(p.on);
      }),
      ws.on("roulette.ended", (p) => {
        if (sessionRef.current && p.session_id !== sessionRef.current) return;
        stopAv();
        setSession(null);
        setPartnerTyping(false);
        if (p.reason_class === "vibe_match") {
          toast(copy.roulette.vibe_match, "success");
          navigate("/matches");
          return;
        }
        if (p.end_card) {
          setEndCard({
            session_id: p.end_card.session_id,
            partner_display_name: p.end_card.partner_display_name,
            reason_class: p.reason_class,
          });
          setPhase("ended");
        } else {
          setPhase("gate");
        }
        void refresh();
      }),
      ws.on("roulette.gate", (p) => {
        setGateUntil(p.until);
      }),
      ws.on("roulette.queue_timeout", () => {
        setQueueEmpty(true);
        setPhase("gate");
      }),
      ws.on("roulette.sdp", (p) => {
        const av = avRef.current;
        if (av && (p as { session_id: string }).session_id === sessionRef.current) {
          void handleRemoteSdp(av, sessionRef.current!, (p as { desc: { type: string; sdp: string } }).desc);
        }
      }),
      ws.on("roulette.ice", (p) => {
        const av = avRef.current;
        if (av && (p as { session_id: string }).session_id === sessionRef.current) {
          void handleRemoteIce(av, (p as { candidate: unknown }).candidate);
        }
      }),
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [navigate, refresh, stopAv]);

  // Rotating searching lines while queued.
  useEffect(() => {
    if (phase !== "queued") return;
    const t = setInterval(() => setSearchLine((i) => (i + 1) % copy.roulette.searching.length), 2600);
    return () => clearInterval(t);
  }, [phase]);

  // In-session keepalive (feeds the server's idle-disconnect sweep) + best-effort leave on exit.
  useEffect(() => {
    if (phase !== "session" || !session) return;
    const keepalive = setInterval(() => {
      void wsCommand("roulette.typing", { session_id: session.session_id, on: false }).catch(() => {});
    }, 40_000);
    const onHide = () => {
      void wsCommand("roulette.leave", {}).catch(() => {});
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      clearInterval(keepalive);
      window.removeEventListener("pagehide", onHide);
    };
  }, [phase, session]);

  // AV bring-up (relay-only; only when server flag + both opted in ⇒ mode 'av').
  useEffect(() => {
    if (phase !== "session" || !session || session.mode !== "av" || avRef.current) return;
    let cancelled = false;
    void (async () => {
      const iceServers = await fetchRouletteIce();
      if (!iceServers || cancelled) return;
      try {
        const av = await startRouletteAv({
          sessionId: session.session_id,
          initiator,
          iceServers,
          camera: camOn,
          mic: micOn,
          onRemoteStream: (stream) => {
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
          },
        });
        if (cancelled) {
          av.close();
          return;
        }
        avRef.current = av;
        if (localVideoRef.current) localVideoRef.current.srcObject = av.localStream;
      } catch {
        toast(resolveCopy("call.perm_denied")?.replace("{mic_camera}", "camera/mic") ?? "Camera blocked", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, session, initiator, camOn, micOn]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [lines, partnerTyping]);

  // ── actions ────────────────────────────────────────────────────────────────
  const enter = async () => {
    setQueueEmpty(false);
    try {
      await wsCommand("roulette.join", { av: camOn || micOn });
      setPhase("queued");
    } catch (err) {
      if (err instanceof WsCommandError) {
        if (err.code === "GATED") {
          await refresh();
          toast(formatCopy(copy.roulette.slow_down, { s: gateSeconds || 30 }), "error");
          return;
        }
        if (err.code === "NEED_VERIFIED") {
          toast(copy.roulette.need_verified, "error");
          return;
        }
        toast(err.message, "error");
      }
    }
  };

  const cancelQueue = async () => {
    try {
      await wsCommand("roulette.leave", {});
    } catch {
      /* server refresh below settles it */
    }
    setPhase("gate");
    void refresh();
  };

  const send = async (anyway = false) => {
    const body = (anyway ? pendingAnyway : draft)?.trim();
    if (!body || !session) return;
    try {
      const res = await wsCommand<{ folded: boolean }>("roulette.msg", {
        session_id: session.session_id,
        body,
        ...(anyway ? { send_anyway: true } : {}),
      });
      setLines((ls) => [...ls, { key: crypto.randomUUID(), from: "me", body, folded: res?.folded }]);
      setDraft("");
      setPendingAnyway(null);
    } catch (err) {
      if (err instanceof WsCommandError) {
        if (err.code === "SEND_ANYWAY_REQUIRED") {
          setPendingAnyway(body);
          return;
        }
        if (err.code === "RATE_LIMITED") {
          toast(resolveCopy("chat.rate_limited") ?? "Slow down.", "error"); // input kept
          return;
        }
        if (err.code === "SESSION_OVER") {
          void refresh();
          return;
        }
        toast(err.message, "error");
      }
    }
  };

  const skip = async () => {
    if (!session) return;
    try {
      await wsCommand("roulette.skip", { session_id: session.session_id });
    } catch {
      void refresh();
    }
  };

  const vibe = async () => {
    if (!session || vibeSent) return;
    try {
      const res = await wsCommand<{ vibe: string }>("roulette.vibe", { session_id: session.session_id });
      setVibeSent(true);
      if (res?.vibe === "matched") {
        toast(copy.roulette.vibe_match, "success");
        navigate("/matches");
      }
    } catch (err) {
      if (err instanceof WsCommandError && err.code === "SESSION_OVER") void refresh();
    }
  };

  const leaveSession = async () => {
    stopAv();
    try {
      await wsCommand("roulette.leave", {});
    } catch {
      /* refresh settles */
    }
    void refresh();
  };

  const next = async () => {
    setEndCard(null);
    await enter();
  };

  const onDraftChange = (v: string) => {
    setDraft(v);
    if (session) {
      void wsCommand("roulette.typing", { session_id: session.session_id, on: v.length > 0 }).catch(() => {});
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="roulette roulette--center">
        <PitWheel />
      </div>
    );
  }

  const recent = status?.recent_partners ?? [];
  const recourseSheets = (
    <>
      <ReportSheet
        open={reportFor !== null}
        onClose={() => setReportFor(null)}
        subject={{ sessionId: reportFor?.sessionId, displayName: reportFor?.name }}
      />
      <BlockConfirmSheet
        open={blockFor !== null}
        onClose={() => setBlockFor(null)}
        target={{ sessionId: blockFor?.sessionId, displayName: blockFor?.name }}
        onBlocked={() => {
          setBlockFor(null);
          void refresh();
        }}
      />
    </>
  );

  // — end card (REQUEUE_GATE) —
  if (phase === "ended" && endCard) {
    return (
      <div className="roulette roulette--center">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={springs.dramatic}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Card className="roulette__endcard">
            <h2 className="roulette__endcard-title type-display-3">
              {formatCopy(copy.roulette.end_card, { name: endCard.partner_display_name })}
            </h2>
            <p className="roulette__muted type-editorial">{copy.roulette.partner_left}</p>
            {/* recourse — dead serious, one tap away (copy-tone §1.4) */}
            <div className="roulette__endcard-recourse">
              <Button
                variant="danger"
                onClick={() => setReportFor({ sessionId: endCard.session_id, name: endCard.partner_display_name })}
              >
                {formatCopy(copy.roulette.end_report_cta, { name: endCard.partner_display_name })}
              </Button>
              <p className="roulette__fineprint">{copy.roulette.end_report_cta_sub}</p>
              <Button
                variant="ghost"
                onClick={() => setBlockFor({ sessionId: endCard.session_id, name: endCard.partner_display_name })}
              >
                {formatCopy(copy.roulette.end_block_cta, { name: endCard.partner_display_name })}
              </Button>
              <p className="roulette__fineprint">{copy.roulette.end_block_cta_sub}</p>
            </div>
            <div className="roulette__endcard-actions">
              {gateSeconds > 0 ? (
                <Button disabled className="type-numeric">
                  {formatCopy(copy.roulette.slow_down, { s: gateSeconds })}
                </Button>
              ) : (
                <Button variant="primary" onClick={() => void next()}>
                  {resolveCopy("roulette.next_stranger_label") ?? "Next stranger"}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setPhase("gate")}>
                {resolveCopy("roulette.leave_pit_label") ?? "Leave the pit"}
              </Button>
            </div>
          </Card>
        </motion.div>
        {recourseSheets}
      </div>
    );
  }

  // — queue —
  if (phase === "queued") {
    return (
      <div className="roulette roulette--center">
        <div className="roulette__searching crt">
          <p className="roulette__search-kicker h-caps">
            {resolveCopy("roulette.queue_kicker") ?? "matchmaking"}
          </p>
          <PitWheel />
          <AnimatePresence mode="wait">
            <motion.p
              key={searchLine}
              className="roulette__searchline type-editorial"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              {copy.roulette.searching[searchLine]}
            </motion.p>
          </AnimatePresence>
          <Button variant="ghost" onClick={() => void cancelQueue()}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // — live session —
  if (phase === "session" && session) {
    const partner = session.partner;
    const live = session.state === "live";
    return (
      <div className="roulette roulette--session">
        <header className="roulette__partner">
          {live ? (
            <span className="roulette__live-chip" aria-hidden="true">
              <i />
              {resolveCopy("roulette.live_badge") ?? "LIVE"}
            </span>
          ) : null}
          <span className="roulette__partner-name">
            {partner.display_name}
            {partner.age !== null ? `, ${partner.age}` : ""}
          </span>
          {partner.verified ? <VerifiedBadge /> : null}
          {!live ? <Badge>{resolveCopy("roulette.connecting_badge") ?? "connecting…"}</Badge> : null}
        </header>

        {session.mode === "av" ? (
          <div className="roulette__videos crt">
            <video ref={remoteVideoRef} className="roulette__video roulette__video--remote" autoPlay playsInline />
            <video ref={localVideoRef} className="roulette__video roulette__video--local" autoPlay playsInline muted />
          </div>
        ) : null}

        <div ref={logRef} className="roulette__log" role="log" aria-live="polite">
          {lines.map((l) =>
            l.folded && !l.revealed && l.from === "partner" ? (
              <button
                key={l.key}
                className="roulette__line roulette__line--folded"
                onClick={() =>
                  setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, revealed: true } : x)))
                }
              >
                {resolveCopy("chat.hidden_by_filters") ?? "Hidden by your filters — tap to reveal"}
              </button>
            ) : (
              <motion.p
                key={l.key}
                className={`roulette__line roulette__line--${l.from}`}
                initial={{ opacity: 0, y: 10, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={springs.bouncy}
                style={{ transformOrigin: l.from === "me" ? "bottom right" : "bottom left" }}
              >
                {l.body}
              </motion.p>
            ),
          )}
          {partnerTyping ? (
            <p className="roulette__line roulette__line--typing">
              <span className="typing-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </p>
          ) : null}
        </div>

        {pendingAnyway !== null ? (
          <div className="roulette__interstitial">
            <p>{resolveCopy("chat.send_anyway") ?? "Sure about that? It reads rough."}</p>
            <div className="roulette__interstitial-actions">
              <Button variant="ghost" onClick={() => setPendingAnyway(null)}>
                Rewrite it
              </Button>
              <Button variant="danger" onClick={() => void send(true)}>
                Send anyway
              </Button>
            </div>
          </div>
        ) : null}

        <form
          className="roulette__composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <input
            className="roulette__input"
            value={draft}
            maxLength={2000}
            placeholder={resolveCopy("roulette.input_placeholder") ?? "Say something…"}
            onChange={(e) => onDraftChange(e.target.value)}
          />
          <Button type="submit" disabled={!draft.trim()}>
            {resolveCopy("roulette.send_label") ?? "Send"}
          </Button>
        </form>

        <div className="roulette__controls">
          <Button onClick={() => void skip()}>{resolveCopy("roulette.next_label") ?? "Next ▸"}</Button>
          <Button
            variant={vibeSent ? "ghost" : "primary"}
            className={!vibeSent && live ? "roulette__vibe roulette__vibe--armed" : "roulette__vibe"}
            disabled={vibeSent || !live}
            onClick={() => void vibe()}
          >
            {vibeSent
              ? (resolveCopy("roulette.vibe_sent_label") ?? "Vibe sent")
              : (resolveCopy("roulette.vibe_label") ?? "Vibe Check")}
          </Button>
          {/* safety verbs — calm, serious, always present (MISSION §2) */}
          <Button
            variant="danger"
            onClick={() => setReportFor({ sessionId: session.session_id, name: partner.display_name })}
          >
            {resolveCopy("roulette.report_label") ?? "Report"}
          </Button>
          <Button
            variant="danger"
            onClick={() => setBlockFor({ sessionId: session.session_id, name: partner.display_name })}
          >
            {resolveCopy("roulette.block_label") ?? "Block"}
          </Button>
          <Button variant="ghost" onClick={() => void leaveSession()}>
            {resolveCopy("roulette.leave_label") ?? "Leave"}
          </Button>
        </div>
        {recourseSheets}
      </div>
    );
  }

  // — gate screen —
  const blockedReason = status && !status.eligible ? status.reason : null;
  return (
    <div className="roulette">
      <div className="roulette__stage">
        <motion.div
          className="roulette__marquee crt crt--glow"
          initial={{ opacity: 0, y: 18, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={springs.dramatic}
        >
          <h1 className="roulette__title type-display-1">
            {resolveCopy("roulette.title") ?? "THE PIT"}
          </h1>
          <p className="roulette__tagline type-editorial">
            {resolveCopy("roulette.tagline") ?? "Random verified strangers."}
          </p>
        </motion.div>
        <Card className="roulette__rules">
          <p>{copy.roulette.rules}</p>
        </Card>

        {!rouletteEnabled ? (
          <Card className="roulette__blocked">
            <p>{resolveCopy("features.roulette_off_note") ?? "The pit is switched off in your settings."}</p>
            <Link to="/settings/features" className="btn">
              {resolveCopy("roulette.disabled_cta") ?? "Reopen it in settings"}
            </Link>
          </Card>
        ) : blockedReason === "need_verified" ? (
          <Card className="roulette__blocked">
            <p>{copy.roulette.need_verified}</p>
            <Button onClick={() => navigate("/me")}>
              {resolveCopy("roulette.verify_cta") ?? "Get verified"}
            </Button>
          </Card>
        ) : blockedReason === "restricted" ? (
          <Card className="roulette__blocked">
            <p>
              {formatCopy(copy.roulette.restricted, {
                date: status?.restricted_until ? new Date(status.restricted_until).toLocaleString() : "later",
              })}
            </p>
          </Card>
        ) : blockedReason === "cooldown" ? (
          <Card className="roulette__blocked">
            <Button onClick={() => navigate("/cooldown")}>
              {resolveCopy("roulette.cooldown_cta") ?? "You're in cooldown"}
            </Button>
          </Card>
        ) : blockedReason ? (
          <Card className="roulette__blocked">
            <p>{resolveCopy("roulette.profile_hold")}</p>
          </Card>
        ) : (
          <>
            {status?.av_enabled ? (
              <div className="roulette__toggles">
                <label className="roulette__toggle">
                  <input type="checkbox" checked={camOn} onChange={(e) => setCamOn(e.target.checked)} />
                  Camera
                </label>
                <label className="roulette__toggle">
                  <input type="checkbox" checked={micOn} onChange={(e) => setMicOn(e.target.checked)} />
                  Mic
                </label>
                <span className="roulette__fineprint">
                  {resolveCopy("roulette.av_optional_note") ?? "Optional. Text always works."}
                </span>
              </div>
            ) : (
              <p className="roulette__fineprint roulette__fineprint--center">
                {resolveCopy("roulette.av_disabled_note") ??
                  "Text mode tonight — cameras arrive once the relay is verified."}
              </p>
            )}
            {gateSeconds > 0 ? (
              <Button disabled className="type-numeric">
                {formatCopy(copy.roulette.slow_down, { s: gateSeconds })}
              </Button>
            ) : (
              <span className="roulette__enter-wrap">
                <Button size="lg" variant="primary" className="roulette__enter" onClick={() => void enter()}>
                  {copy.roulette.enter}
                </Button>
              </span>
            )}
          </>
        )}

        {queueEmpty ? (
          <p className="roulette__muted type-editorial">{copy.roulette.queue_empty}</p>
        ) : null}

        {recent.length > 0 ? (
          <section className="roulette__recent">
            <h2 className="roulette__recent-title h-caps">{copy.roulette.recent_partners}</h2>
            <ul className="roulette__recent-list">
              {recent.map((r) => (
                <li key={r.session_id} className="roulette__recent-row">
                  <span className="roulette__recent-name">{r.display_name}</span>
                  <span className="roulette__recent-time type-numeric">
                    {new Date(r.ended_at).toLocaleTimeString()}
                  </span>
                  <button
                    className="roulette__recent-action"
                    onClick={() => setReportFor({ sessionId: r.session_id, name: r.display_name })}
                  >
                    Report
                  </button>
                  <button
                    className="roulette__recent-action"
                    onClick={() => setBlockFor({ sessionId: r.session_id, name: r.display_name })}
                  >
                    Block
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      {recourseSheets}
    </div>
  );
}
